/* eslint-disable camelcase */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DifficultQuestion
 *
 * @file  DifficultQuestion.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Typography } from '@material-ui/core';
import useDifficultTopicStyles from './styles/DifficultTopicStyles';

const DifficultQuestion = ({
  difficultQuestion,
  metaData
}) => {
  const classes = useDifficultTopicStyles();

  const renderAvatar = (avatarSrc, name) => {
    if (avatarSrc) {
      return <Avatar alt={name} className={classes.avatar} src={avatarSrc} />;
    } if (name) {
      return <Avatar className={classes.avatar}>{name[0]}</Avatar>;
    }

    return <Avatar className={classes.avatar}>?</Avatar>;
  };

  const renderUserHeader = () => {
    if (!metaData.user || !metaData.topics) return null;

    const {
      avatarSrc,
      name
    } = metaData.user;

    const topics = metaData.topics.length > 0 ? `From ${metaData.topics.join(', ')}` : '';

    return (
      <div className={classes.userHeaderWrapper}>
        <div className={classes.userHeader}>
          {renderAvatar(avatarSrc, name)}
          <Typography className={classes.userHeaderText}>{name}</Typography>
        </div>

        <div className={classes.userHeaderTopics}>
          {topics}
        </div>
      </div>
    );
  };

  const renderQuestionHeader = () => {
    if (!metaData.totalQuestions || !metaData.totalGradedQuestions) return null;

    const headerText = `Graded question ${metaData.totalGradedQuestions} of ${metaData.totalQuestions}`;

    return (
      <div className={classes.questionHeader}>
        <Typography className={classes.questionHeaderText}>{headerText}</Typography>
      </div>
    );
  };

  const renderQuestion = () => {
    const {
      question_type,
      question_stem,
      options
    } = difficultQuestion;

    return (
      <div className={classes.questionWrapper}>
        <Typography className={classes.questionText}>{question_stem}</Typography>
        {question_type === 'multiple_choice' && (
          <Fragment>
            {options.map((option) => (
              <div key={option.id}>
                <Typography className={`${classes.optionText} ${option.is_most_common_incorrect_response ? classes.mostCommonIncorrectResponse : ''}`}>{option.feedback}</Typography>
                {option.is_most_common_incorrect_response && (
                  <Typography className={classes.mostCommonIncorrectResponseText}>
                    Most common incorrect response
                  </Typography>
                )}
                {option.correct_choice && option.correct_choice === 'Yes' && (
                  <Typography className={classes.correctChoiceText}>
                    Correct answer
                  </Typography>
                )}
              </div>
            ))}
          </Fragment>
        )}
      </div>
    );
  };

  return (
    <div className={classes.questionRoot}>
      {renderUserHeader()}
      <div className={classes.questionContentWrapper}>
        {renderQuestionHeader()}
        {renderQuestion()}
      </div>
    </div>
  );
};

DifficultQuestion.propTypes = {
  difficultQuestion: PropTypes.object,
  metaData: PropTypes.object
};

DifficultQuestion.defaultProps = {
  difficultQuestion: {},
  metaData: {}
};

export default DifficultQuestion;
